import React from 'react';
import { Table, Tag, Checkbox } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { RawSku } from '../types';

interface Props {
  skus:               RawSku[];
  selectedSkuIds:     number[];
  toggleSkuSelection: (id: number) => void;
  toggleSelectAll:    (ids: number[]) => void;
  loading:            boolean;
}

const SkuSelectionTable: React.FC<Props> = ({
  skus,
  selectedSkuIds,
  toggleSkuSelection,
  toggleSelectAll,
  loading,
}) => {
  const visibleIds    = skus.map((s) => s.id);
  const allSelected   = visibleIds.length > 0 && visibleIds.every((id) => selectedSkuIds.includes(id));
  const someSelected  = visibleIds.some((id) => selectedSkuIds.includes(id));

  const columns: ColumnsType<RawSku> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={!allSelected && someSelected}
          onChange={() => toggleSelectAll(visibleIds)}
        />
      ),
      key:   'select',
      width: 48,
      render: (_: unknown, record: RawSku) => (
        <Checkbox
          checked={selectedSkuIds.includes(record.id)}
          onChange={() => toggleSkuSelection(record.id)}
        />
      ),
    },
    {
      title:     'SKU Name',
      dataIndex: 'name',
      key:       'name',
      sorter:    (a: RawSku, b: RawSku) => a.name.localeCompare(b.name),
    },
    {
      title:  'Buying Model',
      key:    'buying_model',
      width:  130,
      render: (_: unknown, record: RawSku) => {
        const model = (record.subscription_buying_model || '').toUpperCase();
        const color = model === 'EA' ? 'blue' : model === 'ALC' ? 'orange' : 'default';
        return model ? <Tag color={color}>{model}</Tag> : <Tag>—</Tag>;
      },
    },
    {
      title:  'External',
      key:    'is_external',
      width:  90,
      render: (_: unknown, record: RawSku) => (
        <Tag color={record.is_external ? 'green' : 'default'}>
          {record.is_external ? 'Yes' : 'No'}
        </Tag>
      ),
    },
  ];

  return (
    <Table<RawSku>
      rowKey="id"
      loading={loading}
      columns={columns}
      dataSource={skus}
      pagination={{ pageSize: 20, showSizeChanger: false, showTotal: (t) => `${t} SKUs` }}
      onRow={(record) => ({
        onClick: () => toggleSkuSelection(record.id),
        style:   { cursor: 'pointer' },
      })}
      size="small"
      scroll={{ y: 400 }}
    />
  );
};

export default SkuSelectionTable;
